import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	Output
}                      from '@angular/core';
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR
}                      from '@angular/forms';
import { NumberUtils } from '@utils/helpers';

@Component( {
	selector: 'app-input-number',
	templateUrl: './input-number.component.html',
	styleUrls: [ './input-number.component.scss' ],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef( () => InputNumberComponent ),
			multi: true
		}
	]
} )
export class InputNumberComponent implements ControlValueAccessor {

	private _skipSetNumberValueCall: boolean;
	private _spinnerEventInterval;
	private _spinnerEventTimeout;
	private _revertValue: number;
	private _numberInputIsNotInRange: boolean;
	private _numberStep = 1;
	private _numberValue: number;

	numberStringValue: string;
	showRevertButton: boolean;
	showClearButton: boolean;

	@Input() id: string;
	@Input() title: string;
	@Input() placeholder: string;
	@Input() tabindex = 0;
	@Input() inputWrapperStyleClass = '';
	@Input() inputStyleClass = '';

	@Input() isFocused: boolean;
	@Input() isHovered: boolean;
	@Input() hasError: boolean;
	@Input() disabled: boolean;
	@Input() readOnly: boolean;

	@Input() minNumber: number;
	@Input() maxNumber: number;


	@Input() set numberStep( numberStep: number ) {
		this._numberStep = numberStep ?? 1;
	}

	get numberStep(): number {
		return this._numberStep;
	}

	@Input() set numberValue( numberValue: number ) {

		if ( typeof numberValue === 'string' ) {
			numberValue = Number.parseInt( numberValue, 10 );
		}

		if ( Number.isNaN( numberValue ) ) { return; }

		this.setNumberValue( numberValue, true, true );
	}

	get numberValue(): number {
		return this._numberValue;
	}

	@Output() inputChangeEvent: EventEmitter<number> = new EventEmitter<number>();

	constructor(
		private changeDetectorRef: ChangeDetectorRef
	) { }

	private emitInputChange(): void {
		this.inputChangeEvent.emit( this._numberValue );
	}

	private setNumberValue( numberValue: number, updateRevertValue: boolean = true, blockEmit: boolean = false ): void {
		this.numberStringValue = `${ this._numberValue ?? '' }`; // removes leading zeros of input value

		if ( this._numberValue === numberValue ) { return; }

		// sanitize number value
		this._numberValue = numberValue;
		this.validateAndSanitizeNumberInput();
		this.numberStringValue = `${ this._numberValue ?? '' }`; // update input value in case of a sanitation change

		// save revert value
		this._revertValue = updateRevertValue
												? this._numberValue
												: this._revertValue;

		// for ControlValueAccessor
		this._skipSetNumberValueCall = true;
		this.onChange( this._numberValue );

		// update buttons and emit
		this.updateActionButtonsVisibility();
		if ( !blockEmit ) {
			this.emitInputChange();
		}
	}

	private validateAndSanitizeNumberInput(): void {
		this._numberInputIsNotInRange = !NumberUtils.numberIsInRange(
			this._numberValue,
			this.minNumber,
			this.maxNumber,
			{
				checkMin: NumberUtils.numbersAreIntegers( this.minNumber ),
				checkMax: NumberUtils.numbersAreIntegers( this.maxNumber )
			}
		);

		if ( this._numberInputIsNotInRange ) {
			this._numberValue = NumberUtils.setNumberValueInsideRange(
				this._numberValue,
				this.minNumber,
				this.maxNumber
			);
		}
	}

	private updateActionButtonsVisibility() {
		if ( Number.isInteger( this._numberValue ) && this._numberValue !== this.minNumber ) {
			this.showClearButton = true;
			this.showRevertButton = !this.showClearButton;
		} else {
			this.showClearButton = false;
			this.showRevertButton = Number.isInteger( this._revertValue ) && this._numberValue !== this._revertValue;
		}
	}

	private increaseControlValue(): void {
		let numberValue = Number.isInteger( this._numberValue ) ? this._numberValue : 0;
		numberValue += this.numberStep;
		numberValue = NumberUtils.setNumberValueInsideRange(
			numberValue,
			null,
			this.maxNumber
		);

		this.setNumberValue( numberValue );
	}

	private decreaseControlValue(): void {
		let numberValue = Number.isInteger( this._numberValue ) ? this._numberValue : 0;
		numberValue -= this.numberStep;
		numberValue = NumberUtils.setNumberValueInsideRange(
			numberValue,
			this.minNumber,
			null
		);

		this.setNumberValue( numberValue );
	}


	onSpinnerEvent( isClicked: boolean, type: 'increase' | 'decrease' ): void {

		// if not detached the mousedown event for single click is not triggered, due to template change updates
		// reattach changeDetector after action buttons visibility is handled
		this.changeDetectorRef.detach();

		if ( isClicked ) {
			switch ( type ) {
				case 'increase':
					this.increaseControlValue();
					break;
				case 'decrease':
					this.decreaseControlValue();
					break;
			}
		} else {
			this.changeDetectorRef.reattach();
		}

		if ( this._spinnerEventTimeout ) { clearTimeout( this._spinnerEventTimeout ); }
		if ( this._spinnerEventInterval ) { clearInterval( this._spinnerEventInterval ); }

		if ( isClicked ) {
			this._spinnerEventTimeout = setTimeout( () => {
				clearTimeout( this._spinnerEventTimeout );
				this.changeDetectorRef.reattach();

				this._spinnerEventInterval = setInterval( () => {
					if (
						this.maxNumber && !NumberUtils.numberIsInMaxRange( this._numberValue, this.maxNumber, false ) ||
						this.minNumber && !NumberUtils.numberIsInMinRange( this._numberValue, this.minNumber, false )
					) {
						clearInterval( this._spinnerEventInterval );
						return;
					}

					switch ( type ) {
						case 'increase':
							this.increaseControlValue();
							break;
						case 'decrease':
							this.decreaseControlValue();
							break;
					}
				}, 100 );
			}, 1000 );
		}
	}

	clearValue(): void {
		this._revertValue = this._numberValue;
		this.setNumberValue( null, false );
	}

	revertValue(): void {
		this.setNumberValue( this._revertValue, false );
		this._revertValue = null;
	}

	onInputChange( number ): void {
		const timeout = setTimeout( () => {
			clearTimeout( timeout );

			Number.isInteger( number )
			? this.setNumberValue( number )
			: this.clearValue();
		}, 0 );
	}


	// ControlValueAccessor ############################################################
	writeValue( numberValue: number ): void {
		if ( !this._skipSetNumberValueCall ) {
			this.setNumberValue( numberValue, true, true );
		}
		this._skipSetNumberValueCall = false;
	}

	registerOnChange( fn: any ): void {
		this.onChange = fn;
	}

	registerOnTouched( fn: any ): void {
		this.onTouched = fn;
	}

	setDisabledState?( isDisabled: boolean ): void {
		this.disabled = isDisabled;
	}

	onChange: any = () => {};

	onTouched: any = () => {};
}
